// usePersonnelHistory.js - Fetch and expose today's activity history for a
// single employee.  Consumed by PersonnelActivityHistory modal.
//
// ── Usage ─────────────────────────────────────────────────────────────────────
//
//   const { activityHistory, count, loading, error } =
//     usePersonnelHistory(person?.employeeId);
//
// Pass `null` or `undefined` to skip the fetch (e.g. when no employee is
// selected).  The hook re-fetches automatically whenever employeeId changes.
//
// ── Data flow ─────────────────────────────────────────────────────────────────
//
//   personnelActivityHistory.js (person prop)
//     → usePersonnelHistory(person.employeeId)
//       → fetchActivityHistory({ employeeId })   [dataReceiver.js]
//         → GET activity-personnel?employeeId=…  [dataServe.js]
//           → RTLS_PersonnelMetricsActivityHist  [SQL]

import { useState, useEffect, useRef } from "react";
import { fetchActivityHistory } from "../../utils/dataReceiver";

// ── Constants ─────────────────────────────────────────────────────────────────

// Keeps the fallback array reference stable so consumers that spread
// activityHistory into useTableSort don't trigger unnecessary re-renders.
const EMPTY_HISTORY = [];

// ── Hook ──────────────────────────────────────────────────────────────────────

/**
 * Fetch today's job activity history for a single employee.
 *
 * @param {string | null | undefined} employeeId
 *   The EmpID to query (sourced from person.employeeNumber on the Personnel
 *   Metrics person object — mapped from RTLS properties.EmployeeID).
 *
 * @returns {{
 *   activityHistory: Array<{
 *     jobId:     string | null,
 *     jobName:   string | null,
 *     startTime: string | null,
 *     endTime:   string | null,
 *     duration:  string | null,
 *     hours:     number | null,
 *     eventDate: string | null,
 *   }>,
 *   count:   number,
 *   loading: boolean,
 *   error:   Error | null,
 * }}
 */
function usePersonnelHistory(employeeId) {
  console.log("[usePersonnelHistory] received employeeId:", employeeId);
  const [activityHistory, setActivityHistory] = useState(EMPTY_HISTORY);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Stable ref to the in-flight AbortController so we can cancel on cleanup
  // or when employeeId changes before the previous fetch resolves.
  const abortRef = useRef(null);

  useEffect(() => {
    // ── Guard: skip fetch if no employeeId provided ────────────────────────
    if (!employeeId?.trim()) {
      setActivityHistory(EMPTY_HISTORY);
      setCount(0);
      setLoading(false);
      setError(null);
      return;
    }

    // ── Cancel any in-flight request from a previous render ────────────────
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    // ── Fetch ──────────────────────────────────────────────────────────────
    setLoading(true);
    setError(null);

    fetchActivityHistory({ employeeId: employeeId.trim() }, controller.signal)
      .then(({ activityHistory: rows, count: rowCount }) => {
        setActivityHistory(rows);
        setCount(rowCount);
      })
      .catch((err) => {
        // AbortError is a normal cancellation (component unmounted or
        // employeeId changed) — swallow it silently.
        if (err.name === "AbortError") return;
        setError(err);
      })
      .finally(() => {
        setLoading(false);
      });

    // ── Cleanup: cancel if employeeId changes or component unmounts ────────
    return () => {
      controller.abort();
    };
  }, [employeeId]);

  return { activityHistory, count, loading, error };
}

export default usePersonnelHistory;
